import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../../common/types';
import { Cover } from '../../../music24/components/primitives/Cover';
import { catalogArtworkSrc } from '../catalogArtwork';
import { formatCatalogDay } from '../catalogDates';
import { MergeGetLine, MergePair, mergeGetLines } from './mergeRules';
import { mergeMessages } from './mergeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the merge dialogs; each part uses its own slice of it */
import styles from './Merge.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MergeConfirmStepProps {
  pair: MergePair;
}

/** The confirm body (design catalog-merge a3): the row that stays, the row that goes, what moves. */
const MergeConfirmStepComponent: React.FC<MergeConfirmStepProps> = ({ pair }) => {
  const intl = useIntl();
  const { kept, leaving } = pair;
  const lines = useMemo(() => mergeGetLines(pair), [pair]);

  const getText = (line: MergeGetLine): string => {
    switch (line.kind) {
      case 'link':
        return intl.formatMessage(mergeMessages.getsLink, { service: line.service });
      case 'date':
        return intl.formatMessage(mergeMessages.getsDate, { date: formatCatalogDay(intl, line.date) });
      case 'cover':
        return intl.formatMessage(mergeMessages.getsCover, { title: kept.title });
      case 'made':
        return intl.formatMessage(mergeMessages.getsMade, { title: leaving.title });
    }
  };

  return (
    <div className={styles.stack}>
      <div className={styles.pair}>
        <div className={clsx(styles.side, styles.sideKeep)} data-testid="merge-kept">
          <span className={clsx(styles.sideLabel, styles.sideLabelKeep)}>
            {intl.formatMessage(mergeMessages.stays)}
          </span>
          <div className={styles.sideRow}>
            <Cover src={catalogArtworkSrc(kept, 46)} label={kept.title} size={46} />
            <span className={styles.optionName}>{kept.title}</span>
          </div>
        </div>
        <div className={clsx(styles.side, styles.sideGo)} data-testid="merge-leaving">
          <span className={styles.sideLabel}>{intl.formatMessage(mergeMessages.leaves)}</span>
          <div className={styles.sideRow}>
            <Cover src={catalogArtworkSrc(leaving, 46)} label={leaving.title} size={46} />
            <span className={styles.optionName}>{leaving.title}</span>
          </div>
        </div>
      </div>
      <div className={styles.moves}>
        <div className={styles.move}>
          <span className={styles.sideLabel}>{intl.formatMessage(mergeMessages.keeps, { title: kept.title })}</span>
          {[mergeMessages.keepsLink, mergeMessages.keepsKit, mergeMessages.keepsHistory].map((message) => (
            <span key={message.id} className={styles.moveLine}>
              <FontAwesomeIcon icon={faCheck} className={styles.moveKeep} />
              {intl.formatMessage(message)}
            </span>
          ))}
        </div>
        <div className={styles.move}>
          <span className={styles.sideLabel}>{intl.formatMessage(mergeMessages.gets, { title: kept.title })}</span>
          {lines.map((line) => (
            <span key={`${line.kind}-${line.kind === 'link' ? line.service : ''}`} className={styles.moveLine}>
              <FontAwesomeIcon icon={faPlus} className={styles.moveGet} />
              {getText(line)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MergeConfirmStep = TypedMemo(MergeConfirmStepComponent);
